/**
 * Multi-step wizard for creating a new Account, presented as a centered modal
 * (cbs-portal's Build Group wizard: max-w-3xl card, step dots, footer
 * Cancel/Back + Continue). Client/broker portals create at LEAD / NEW_LEAD;
 * the internal portal ("New Group") creates at the first stage of the
 * internal pipeline, requires picking the tenant, and leads with a Start step
 * offering "Build from the queue" (prefill from a ready deal) or "Start from
 * scratch".
 *
 * Mounted by AccountsList based on the URL ending in `/new`. On successful
 * create, navigates to the new account's detail page.
 */

import {useEffect, useState, type FormEvent, type ReactElement, type ReactNode} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {formatDistanceToNow} from 'date-fns';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  FileText,
  Inbox,
  Loader2,
  PenLine,
  Upload,
  X,
} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Combobox, containsFilter} from '@common-web/components/ui/combobox';
import {StepIndicator} from '@common-web/components/ui/step-indicator';
import {toast} from '@common-web/hooks/use-toast';
import {trpc} from '@common-web/lib/trpc';
import {usePortalActionAllowed} from '@common-web/hooks/use-permissions';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {useAuthStore} from '@common-web/stores/authStore';
import {READY_FOR_CBS_STATUSES} from '@common-web/components/rfc/RfcSubmissionsList';
import {StateSelect, getStateName} from '@client/components/StateSelect';
import {type UsStateCode} from '@common/constants/usStates';
import {MAX_UPLOAD_BYTES} from '@common/constants/files';

type StepKey = 'start' | 'rfc' | 'info' | 'contacts' | 'review';

const STEP_LABEL_BY_KEY: Record<StepKey, string> = {
  start: 'Start',
  rfc: 'RFC Document',
  info: 'Group Info',
  contacts: 'Contacts',
  review: 'Review',
};

/** Extensions we accept for an attached RFC document. */
const RFC_FILE_EXTENSIONS = ['pdf', 'xlsx', 'csv'];

function FormSection({title, children}: {title: ReactNode; children: ReactNode}): ReactElement {
  return (
    <div>
      <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3'>
        {title}
      </p>
      {children}
    </div>
  );
}

function ReviewField({label, value}: {label: string; value: ReactNode}): ReactElement {
  return (
    <div className='flex items-start gap-3 py-1.5'>
      <span className='w-40 shrink-0 text-xs text-muted-foreground'>{label}</span>
      <span className='text-sm'>{value || '\u2014'}</span>
    </div>
  );
}

interface NewAccountModalProps {
  open: boolean;
  onClose: () => void;
  routePrefix: string;
  /** Internal portal only: tenant options for the required client picker. */
  clientOptions?: Array<{id: string; name: string}>;
}

export function NewAccountModal({
  open,
  onClose,
  routePrefix,
  clientOptions,
}: NewAccountModalProps): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const utils = trpc.useUtils();
  const isInternal = useIsInternal();
  const ownAgentId = useAuthStore((s) => s.user?.brokerProfile?.agentId);
  const openQueueFromBanner = searchParams.get('queue') === '1';

  // The RFC document is attached via account.generateUploadUrl/confirmUpload,
  // which require this key (for internal users too) — hide the step from
  // callers who couldn't complete the upload anyway.
  const canUploadDocuments = usePortalActionAllowed(CODE_PERMISSIONS.accounts.uploadDocuments.key);

  // The internal "New Group" flow leads with the demo's Start step (queue vs
  // scratch); client/broker portals go straight to the RFC document step.
  const steps: readonly StepKey[] = (
    isInternal
      ? (['start', 'rfc', 'info', 'contacts', 'review'] as const)
      : (['rfc', 'info', 'contacts', 'review'] as const)
  ).filter((k) => k !== 'rfc' || canUploadDocuments);

  const [step, setStep] = useState(0);
  const stepKey = steps[step];

  // "Build from the queue" path: whether the ready-deals picker is expanded,
  // and the deal that seeded the form (surfaced again on the Review step).
  const [queueOpen, setQueueOpen] = useState(false);
  const [queueRfc, setQueueRfc] = useState<{
    id: string;
    companyName: string;
    clientName: string;
  } | null>(null);

  // CBS Queue banner deep-links with `?queue=1` so the ready-deals picker is
  // already expanded — same list the banner's count is drawn from.
  useEffect(() => {
    if (open && isInternal && openQueueFromBanner) setQueueOpen(true);
  }, [open, isInternal, openQueueFromBanner]);

  // Optional RFC document. Held locally and attached to the account's
  // documents after creation — nothing is prefilled from it.
  const [rfcFile, setRfcFile] = useState<File | null>(null);
  const [rfcFileError, setRfcFileError] = useState<string | null>(null);
  // Agent of record from a picked queue deal, kept for broker auto-match once
  // the scoped broker list loads (internal users must pick the client first).
  const [extractedAgent, setExtractedAgent] = useState<{
    name: string | null;
    email: string | null;
  } | null>(null);

  const [companyName, setCompanyName] = useState('');
  const [state, setState] = useState<UsStateCode | ''>('');
  const [memberCount, setMemberCount] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  // '' = no broker picked yet (the Combobox's empty value).
  const [assignedBrokerId, setAssignedBrokerId] = useState('');
  const [clientId, setClientId] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('');

  // Brokers scoped to whoever is creating the account: admins see every broker
  // in the tenant; brokers see only themselves + their downline.
  const {data: brokers} = trpc.account.listBrokers.useQuery(
    {clientId: isInternal ? clientId || undefined : undefined},
    {enabled: open && (!isInternal || !!clientId)},
  );

  // Default the assignment to the creating broker when they have a profile.
  useEffect(() => {
    if (open && ownAgentId && brokers?.some((b) => b.id === ownAgentId)) {
      setAssignedBrokerId(ownAgentId);
    }
  }, [open, ownAgentId, brokers]);

  // Reset the assigned broker when the tenant changes (internal "New Group"
  // flow): a broker from the previously-selected client is invalid for the new
  // one, and the broker list refetches scoped to the new client. Guarded to the
  // internal flow so it can never race the default self-assignment effect above
  // in the single-tenant client/broker flow (where clientId stays '').
  useEffect(() => {
    if (isInternal) setAssignedBrokerId('');
  }, [isInternal, clientId]);

  // Ready deals awaiting CBS handoff, fetched lazily when the queue picker is
  // expanded. Oldest first — same ordering as the CBS Queue page.
  const queueQuery = trpc.clients.rfc.listForAdmin.useQuery(
    {
      page: 1,
      limit: 50,
      sortKey: 'createdAt',
      sortDirection: 'asc',
      statuses: [...READY_FOR_CBS_STATUSES],
    },
    {enabled: open && isInternal && queueOpen},
  );

  /**
   * Prefill the wizard from a ready deal and jump to Group Info. The broker
   * is matched through the agent auto-match effect (the tenant change resets
   * the broker select, then the scoped list loads).
   */
  function handlePickQueueDeal(rfc: {
    id: string;
    companyName: string | null;
    client: {id: string; name: string};
    assignedBroker: {id: string; name: string; contactEmail: string | null} | null;
  }): void {
    setQueueRfc({
      id: rfc.id,
      companyName: rfc.companyName ?? 'Unnamed deal',
      clientName: rfc.client.name,
    });
    setClientId(rfc.client.id);
    setCompanyName(rfc.companyName ?? '');
    if (rfc.assignedBroker) {
      setExtractedAgent({name: rfc.assignedBroker.name, email: rfc.assignedBroker.contactEmail});
    }
    setStep(steps.indexOf('info'));
  }

  function handleStartFromScratch(): void {
    setQueueRfc(null);
    setQueueOpen(false);
    setStep(steps.indexOf(canUploadDocuments ? 'rfc' : 'info'));
  }

  const generateUploadUrl = trpc.account.generateUploadUrl.useMutation();
  const confirmUpload = trpc.account.confirmUpload.useMutation();

  /**
   * Attach the held RFC document to the newly created account (presigned PUT
   * to S3, then confirm to create the AccountsDocument row). Failures don't
   * block navigation — the account exists; the file can be re-uploaded from
   * the Documents tab.
   */
  async function uploadRfcDocument(accountsId: string, file: File): Promise<void> {
    const contentType = file.type || 'application/octet-stream';
    try {
      const presigned = await generateUploadUrl.mutateAsync({
        accountsId,
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
        category: 'RFC_ATTACHMENT',
      });
      const putRes = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {'Content-Type': contentType},
      });
      if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status}`);
      await confirmUpload.mutateAsync({
        accountsId,
        s3Key: presigned.s3Key,
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
        category: 'RFC_ATTACHMENT',
      });
    } catch {
      toast({
        title: 'RFC document not attached',
        description: `The ${isInternal ? 'group' : 'account'} was created, but the RFC document could not be uploaded. Add it from the Documents tab.`,
        variant: 'destructive',
      });
    }
  }

  const create = trpc.account.create.useMutation({
    // Returning the promise keeps the mutation pending (button shows
    // "Creating…") until the RFC document upload settles.
    onSuccess: async (result) => {
      if (rfcFile) await uploadRfcDocument(result.accountsId, rfcFile);
      utils.account.list.invalidate();
      resetForm();
      onClose();
      navigate(`${routePrefix}/${result.accountsId}`);
    },
  });

  function handleSelectRfcFile(file: File): void {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!RFC_FILE_EXTENSIONS.includes(extension)) {
      setRfcFileError('Unsupported file type — upload a PDF, XLSX, or CSV.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setRfcFileError(
        `File is too large — max ${Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024))} MB.`,
      );
      return;
    }
    setRfcFileError(null);
    setRfcFile(file);
  }

  function clearRfcFile(): void {
    setRfcFile(null);
    setRfcFileError(null);
  }

  // Broker auto-match: once the scoped broker list is available, preselect
  // the queue deal's agent of record — matched by email first, then by exact
  // name — when exactly one broker matches and no broker was chosen yet. Keyed
  // on `brokers` so it re-runs after the internal tenant-change reset effect.
  useEffect(() => {
    if (!extractedAgent || !brokers || assignedBrokerId) return;
    const agentEmail = extractedAgent.email?.trim().toLowerCase();
    const agentName = extractedAgent.name?.trim().toLowerCase();
    const byEmail = agentEmail
      ? brokers.filter((b) => b.contactEmail?.trim().toLowerCase() === agentEmail)
      : [];
    const byName = agentName
      ? brokers.filter((b) => b.name.trim().toLowerCase() === agentName)
      : [];
    const matches = byEmail.length > 0 ? byEmail : byName;
    if (matches.length === 1) {
      setAssignedBrokerId(matches[0].id);
    }
  }, [extractedAgent, brokers, assignedBrokerId]);

  function resetForm(): void {
    setStep(0);
    setQueueOpen(false);
    setQueueRfc(null);
    setRfcFile(null);
    setRfcFileError(null);
    setExtractedAgent(null);
    setCompanyName('');
    setState('');
    setMemberCount('');
    setWebsiteUrl('');
    setAssignedBrokerId('');
    setClientId('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setRole('');
  }

  function handleClose(): void {
    if (create.isPending) return;
    resetForm();
    onClose();
  }

  const groupInfoValid = (!clientOptions || !!clientId) && !!companyName.trim();
  const contactsValid = !!assignedBrokerId;
  const isLastStep = step === steps.length - 1;
  const canAdvance =
    stepKey === 'start'
      ? false // Advances by picking a path card, not the footer button.
      : stepKey === 'info'
        ? groupInfoValid
        : stepKey === 'contacts'
          ? contactsValid
          : true;

  function handleCreate(): void {
    const hasContact = !!(firstName || lastName || email || phone || role);
    create.mutate({
      clientId: isInternal ? clientId : undefined,
      companyName,
      state: state || undefined,
      websiteUrl: websiteUrl || undefined,
      memberCount: memberCount ? Number.parseInt(memberCount, 10) : undefined,
      assignedBrokerId: assignedBrokerId || undefined,
      primaryContact: hasContact
        ? {
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            email: email || undefined,
            phone: phone || undefined,
            role: role || undefined,
          }
        : undefined,
    });
  }

  // Enter in any input advances the step (or creates on the review step).
  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!canAdvance || create.isPending) return;
    if (isLastStep) {
      handleCreate();
    } else {
      setStep(step + 1);
    }
  }

  const selectedClient = clientOptions?.find((c) => c.id === clientId);
  const selectedBroker = brokers?.find((b) => b.id === assignedBrokerId);
  // The Combobox searches on the label, so the email both keeps same-name
  // brokers distinct and makes them searchable by email.
  const brokerItems = (brokers ?? []).map((broker) => ({
    label: broker.contactEmail ? `${broker.name} · ${broker.contactEmail}` : broker.name,
    value: broker.id,
  }));
  const contactName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className='flex max-h-[88vh] w-full max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:rounded-2xl'>
        <form onSubmit={handleSubmit} className='flex min-h-0 flex-1 flex-col'>
          <DialogHeader className='shrink-0 border-b px-6 py-4'>
            <DialogTitle>{isInternal ? 'New Group' : 'New Account'}</DialogTitle>
            <DialogDescription>
              {isInternal
                ? 'Creates an account at the first stage of the internal pipeline. You can add more details after creation.'
                : 'Creates an account in the Prospect phase. You can add more details after creation.'}
            </DialogDescription>
          </DialogHeader>

          <div className='shrink-0'>
            <StepIndicator
              steps={steps.map((k) => STEP_LABEL_BY_KEY[k])}
              currentStep={step}
              variant='primary'
            />
          </div>

          <div className='flex-1 space-y-6 overflow-y-auto px-6 py-5'>
            {stepKey === 'start' && (
              <FormSection title='How do you want to start?'>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  <button
                    type='button'
                    onClick={() => setQueueOpen(true)}
                    className={`flex flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-colors ${
                      queueOpen
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40 hover:bg-muted/40'
                    }`}
                  >
                    <span className='flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary'>
                      <Inbox className='h-4 w-4' />
                    </span>
                    <span className='text-sm font-semibold'>Build from the queue</span>
                    <span className='text-xs text-muted-foreground'>
                      Pick a deal that&rsquo;s ready for CBS and we&rsquo;ll prefill the group
                      details from it.
                    </span>
                  </button>
                  <button
                    type='button'
                    onClick={handleStartFromScratch}
                    className='flex flex-col items-start gap-2 rounded-xl border-2 border-border p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted/40'
                  >
                    <span className='flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary'>
                      <PenLine className='h-4 w-4' />
                    </span>
                    <span className='text-sm font-semibold'>Start from scratch</span>
                    <span className='text-xs text-muted-foreground'>
                      Enter the group&rsquo;s details manually — you can still attach an RFC
                      document on the next step.
                    </span>
                  </button>
                </div>

                {queueOpen ? (
                  <div className='mt-4 overflow-hidden rounded-xl border'>
                    <div className='border-b bg-muted/40 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                      Ready for build
                    </div>
                    {queueQuery.isLoading ? (
                      <div className='flex items-center justify-center gap-2 px-4 py-6 text-sm text-muted-foreground'>
                        <Loader2 className='h-4 w-4 animate-spin' /> Loading queue…
                      </div>
                    ) : queueQuery.error ? (
                      <div className='px-4 py-6 text-center text-sm text-red-600'>
                        Could not load the queue — start from scratch instead.
                      </div>
                    ) : (queueQuery.data?.data.length ?? 0) === 0 ? (
                      <div className='px-4 py-6 text-center text-sm text-muted-foreground'>
                        No deals are waiting for group creation.
                      </div>
                    ) : (
                      <div className='max-h-64 divide-y overflow-y-auto'>
                        {queueQuery.data?.data.map((rfc) => (
                          <button
                            key={rfc.id}
                            type='button'
                            onClick={() => handlePickQueueDeal(rfc)}
                            className='flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-primary/5'
                          >
                            <div className='min-w-0'>
                              <p className='truncate text-sm font-medium'>
                                {rfc.companyName || 'Unnamed deal'}
                              </p>
                              <p className='truncate text-xs text-muted-foreground'>
                                {rfc.client.name}
                                {rfc.assignedBroker?.name ? ` · ${rfc.assignedBroker.name}` : ''}
                              </p>
                            </div>
                            <span className='flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground'>
                              <Clock className='h-3 w-3' />
                              {rfc.createdAt
                                ? formatDistanceToNow(new Date(rfc.createdAt), {addSuffix: true})
                                : '—'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </FormSection>
            )}

            {stepKey === 'rfc' && (
              <FormSection title='RFC document (optional)'>
                <p className='text-sm text-muted-foreground mb-4'>
                  If the {isInternal ? 'group' : 'account'}&rsquo;s completed RFC isn&rsquo;t
                  already in the system, you can upload a copy here and it will be saved to the{' '}
                  {isInternal ? 'group' : 'account'}&rsquo;s documents. Nothing is filled in from it
                  — you&rsquo;ll enter the details in the next steps.
                </p>

                {!rfcFile ? (
                  <>
                    <label className='flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed bg-muted/30 px-4 py-8 transition-colors hover:bg-muted/50'>
                      <Upload className='h-6 w-6 text-brand-blue' />
                      <span className='text-sm font-semibold'>Upload RFC document</span>
                      <span className='text-xs text-muted-foreground'>PDF, XLSX, or CSV</span>
                      <input
                        type='file'
                        accept='.pdf,.xlsx,.csv'
                        className='hidden'
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleSelectRfcFile(f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {rfcFileError ? (
                      <p className='mt-3 text-center text-xs text-red-600'>{rfcFileError}</p>
                    ) : null}
                    <p className='mt-3 text-center text-xs text-muted-foreground'>
                      Or click <span className='font-semibold'>Continue</span> below to skip.
                    </p>
                  </>
                ) : (
                  <div className='rounded-xl border border-emerald-500 bg-emerald-50 p-4'>
                    <div className='flex items-start gap-2.5'>
                      <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-emerald-600' />
                      <div className='min-w-0 flex-1'>
                        <p className='text-sm font-semibold'>RFC document attached</p>
                        <p className='mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground'>
                          <FileText className='h-3 w-3' /> {rfcFile.name}
                        </p>
                        <p className='mt-2 text-xs text-muted-foreground'>
                          It will be saved to the {isInternal ? 'group' : 'account'}&rsquo;s
                          documents after creation.
                        </p>
                      </div>
                      <button
                        type='button'
                        onClick={clearRfcFile}
                        className='shrink-0 rounded p-1 text-muted-foreground hover:bg-black/5'
                        title='Remove document'
                      >
                        <X className='h-4 w-4' />
                      </button>
                    </div>
                  </div>
                )}
              </FormSection>
            )}

            {stepKey === 'info' && (
              <>
                <FormSection title='Group Information'>
                  <div className='space-y-4'>
                    {clientOptions ? (
                      <div className='space-y-1.5'>
                        <Label>Client *</Label>
                        <Select value={clientId || undefined} onValueChange={setClientId}>
                          <SelectTrigger>
                            <SelectValue placeholder='Select client' />
                          </SelectTrigger>
                          <SelectContent>
                            {clientOptions.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className='text-xs text-muted-foreground'>
                          Required — the tenant this group belongs to.
                        </p>
                      </div>
                    ) : null}

                    <div className='space-y-1.5'>
                      <Label htmlFor='companyName'>Company name *</Label>
                      <Input
                        id='companyName'
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        autoFocus
                      />
                      <p className='text-xs text-muted-foreground'>
                        Required — fill in the name of the company.
                      </p>
                    </div>

                    {/* CBS doesn't collect a website when building a group. */}
                    {!isInternal ? (
                      <div className='space-y-1.5'>
                        <Label htmlFor='websiteUrl'>Website</Label>
                        <Input
                          id='websiteUrl'
                          type='text'
                          value={websiteUrl}
                          onChange={(e) => setWebsiteUrl(e.target.value)}
                          placeholder='example.com'
                        />
                      </div>
                    ) : null}
                  </div>
                </FormSection>

                <div className='border-t pt-5'>
                  <FormSection title='Details'>
                    <div className='grid grid-cols-2 gap-3'>
                      <div className='space-y-1.5'>
                        <Label>State</Label>
                        <StateSelect
                          value={state}
                          onValueChange={(v) => setState(v as UsStateCode)}
                          className='w-full'
                        />
                      </div>
                      <div className='space-y-1.5'>
                        <Label htmlFor='memberCount'>Estimated members</Label>
                        <Input
                          id='memberCount'
                          type='number'
                          min='0'
                          value={memberCount}
                          onChange={(e) => setMemberCount(e.target.value)}
                        />
                      </div>
                    </div>
                  </FormSection>
                </div>
              </>
            )}

            {stepKey === 'contacts' && (
              <>
                <FormSection
                  title={
                    <>
                      Primary Contact{' '}
                      <span className='font-normal normal-case tracking-normal'>(optional)</span>
                    </>
                  }
                >
                  <div className='space-y-3'>
                    <div className='grid grid-cols-2 gap-3'>
                      <div className='space-y-1.5'>
                        <Label htmlFor='firstName'>First name</Label>
                        <Input
                          id='firstName'
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                      </div>
                      <div className='space-y-1.5'>
                        <Label htmlFor='lastName'>Last name</Label>
                        <Input
                          id='lastName'
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className='grid grid-cols-2 gap-3'>
                      <div className='space-y-1.5'>
                        <Label htmlFor='email'>Email</Label>
                        <Input
                          id='email'
                          type='email'
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <div className='space-y-1.5'>
                        <Label htmlFor='phone'>Phone</Label>
                        <Input
                          id='phone'
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className='space-y-1.5'>
                      <Label htmlFor='role'>Role</Label>
                      <Input
                        id='role'
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        placeholder='HR Director'
                      />
                    </div>
                  </div>
                </FormSection>

                <div className='border-t pt-5'>
                  <FormSection title='Assigned Broker'>
                    <div className='space-y-1.5'>
                      <Label htmlFor='assignedBroker'>Broker *</Label>
                      <Combobox
                        id='assignedBroker'
                        items={brokerItems}
                        value={assignedBrokerId}
                        setValue={setAssignedBrokerId}
                        message='Select a broker'
                        placeholder='Search by name or email...'
                        emptyMessage='No brokers found.'
                        filter={containsFilter}
                      />
                      <p className='text-xs text-muted-foreground'>
                        Required — assign the account to a broker before creating it.
                      </p>
                    </div>
                  </FormSection>
                </div>
              </>
            )}

            {stepKey === 'review' && (
              <>
                <FormSection title='Group'>
                  {queueRfc ? (
                    <ReviewField
                      label='Queue deal'
                      value={`${queueRfc.companyName} (${queueRfc.clientName})`}
                    />
                  ) : null}
                  {rfcFile ? <ReviewField label='RFC document' value={rfcFile.name} /> : null}
                  {clientOptions ? (
                    <ReviewField label='Client' value={selectedClient?.name} />
                  ) : null}
                  <ReviewField label='Company name' value={companyName.trim()} />
                  <ReviewField label='State' value={state ? getStateName(state) : ''} />
                  <ReviewField label='Estimated members' value={memberCount} />
                  {!isInternal ? <ReviewField label='Website' value={websiteUrl} /> : null}
                </FormSection>

                <div className='border-t pt-5'>
                  <FormSection title='Contacts & Broker'>
                    <ReviewField label='Primary contact' value={contactName} />
                    <ReviewField label='Email' value={email} />
                    <ReviewField label='Phone' value={phone} />
                    <ReviewField label='Role' value={role} />
                    <ReviewField label='Assigned broker' value={selectedBroker?.name} />
                  </FormSection>
                </div>

                {/* Pipeline bookkeeping that CBS doesn't need to confirm on a group build. */}
                {!isInternal ? (
                  <div className='border-t pt-5'>
                    <FormSection title='Pipeline Intake'>
                      <ReviewField label='Entry stage' value='Prospect · New Lead' />
                      <ReviewField label='Creates' value='Pipeline account' />
                    </FormSection>
                  </div>
                ) : null}

                {create.error ? (
                  <div className='text-sm text-red-600'>{create.error.message}</div>
                ) : null}
              </>
            )}
          </div>

          <div className='flex shrink-0 items-center justify-between border-t px-6 py-4'>
            <Button
              type='button'
              variant='outline'
              onClick={() => (step === 0 ? handleClose() : setStep(step - 1))}
              disabled={create.isPending}
            >
              {step === 0 ? (
                'Cancel'
              ) : (
                <>
                  <ArrowLeft className='h-4 w-4 mr-2' />
                  Back
                </>
              )}
            </Button>
            {/* The Start step advances by picking a path card; no Continue. */}
            {stepKey !== 'start' ? (
              <Button type='submit' disabled={!canAdvance || create.isPending}>
                {isLastStep ? (
                  create.isPending ? (
                    'Creating…'
                  ) : isInternal ? (
                    'Create Group'
                  ) : (
                    'Create Account'
                  )
                ) : (
                  <>
                    Continue
                    <ArrowRight className='h-4 w-4 ml-2' />
                  </>
                )}
              </Button>
            ) : null}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
